import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

import PlanDetailCard from '../components/PlanDetailCard';
import InvoiceDetailModal from '../components/InvoiceDetailModal';

const SuperAdminDashboard = () => {
    const location = useLocation();
    const [view, setView] = useState('analytics'); // 'analytics', 'clinics', 'settings', 'audit-logs', 'users'
    const chatEndRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState(''); // Search state

    const [clinics, setClinics] = useState([]);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditStartDate, setAuditStartDate] = useState('');
    const [auditEndDate, setAuditEndDate] = useState('');
    const [users, setUsers] = useState([]); // New: Users list
    const [userFilter, setUserFilter] = useState('All'); // New: User Role Filter
    const [userSearch, setUserSearch] = useState(''); // New: User Search
    const [invoices, setInvoices] = useState([]); // New: Invoices State
    // Invoice State
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoiceStartDate, setInvoiceStartDate] = useState('');
    const [invoiceEndDate, setInvoiceEndDate] = useState('');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [tickets, setTickets] = useState([]); // New: Tickets State
    const [showTicketModal, setShowTicketModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');

    const [loading, setLoading] = useState(true);
    const [ticketStatus, setTicketStatus] = useState('Open');

    // Layout State
    const [sidebarActive, setSidebarActive] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);

    // Modal State (Unified for Create & Edit)
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentClinicId, setCurrentClinicId] = useState(null);

    // View Modal State (Clinic Details)
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewClinicData, setViewClinicData] = useState(null);

    // User Detail Modal State
    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [formData, setFormData] = useState({
        clinicName: '', address: '', contactNumber: '', emergencyContactNumber: '', mapUrl: '', subscriptionPlan: '',
        adminName: '', adminEmail: '', adminPassword: '', adminPhone: ''
    });

    const [plans, setPlans] = useState([]); // New: Plans State
    const [showPlanModal, setShowPlanModal] = useState(false); // New: Plan Modal
    // Plan View Modal State
    const [showPlanViewModal, setShowPlanViewModal] = useState(false);
    const [selectedPlanView, setSelectedPlanView] = useState(null);


    const [planFormData, setPlanFormData] = useState({
        name: '', price: '', currency: 'INR', duration: 'monthly',
        maxDoctors: '', maxStaff: '', storageGB: '', features: ''
    });
    const [editingPlanId, setEditingPlanId] = useState(null);

    // Onboarding Wizard State
    const [onboardingStep, setOnboardingStep] = useState(1);
    const [selectedPlanId, setSelectedPlanId] = useState(null);

    // Settings State
    const [profileForm, setProfileForm] = useState({
        name: '',
        email: '',
        phone: '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [emailStatus, setEmailStatus] = useState('idle'); // idle, checking, available, taken

    const checkEmailAvailability = async (email) => {
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setEmailStatus('idle');
            return;
        }
        setEmailStatus('checking');
        try {
            const response = await api.get('/api/admin/users', {
                params: { search: email }
            });
            // Check for exact match
            const exists = response.data.some(u => u.email.toLowerCase() === email.toLowerCase());
            setEmailStatus(exists ? 'taken' : 'available');
        } catch (error) {
            console.error('Email check failed', error);
            setEmailStatus('idle');
        }
    };

    const toggleSidebar = () => setSidebarActive(!sidebarActive);
    const mobileMenuControl = () => setMobileMenu(!mobileMenu);
    const closeSidebar = () => setMobileMenu(false);




    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/analytics');
            setAnalyticsData(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching analytics:', error);
            setLoading(false);
        }
    }, []);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/plans');
            setPlans(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching plans:', error);
            setLoading(false);
        }
    }, []);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (invoiceSearch) params.append('search', invoiceSearch);
            if (invoiceStartDate) params.append('startDate', invoiceStartDate);
            if (invoiceEndDate) params.append('endDate', invoiceEndDate);

            const response = await api.get(`/api/admin/invoices?${params.toString()}`);
            // FILTER: Only show System/Plan invoices (exclude "Consultation" types) as per User Request
            const planInvoices = response.data.filter(inv =>
                !inv.subscriptionPlan?.toLowerCase().includes('consultation')
            );
            setInvoices(planInvoices);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setLoading(false);
        }
    }, [invoiceSearch, invoiceStartDate, invoiceEndDate]);

    const handleApplyInvoiceFilters = () => {
        fetchInvoices();
    };

    const handleClearInvoiceFilters = () => {
        setInvoiceSearch('');
        setInvoiceStartDate('');
        setInvoiceEndDate('');
        fetchInvoices();
    };



    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/support-tickets');
            setTickets(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            setLoading(false);
        }
    }, []);


    // Added missing function to handle invoice emails
    const handleEmailInvoice = async (invoiceId) => {
        const invoice = invoices.find(inv => inv._id === invoiceId);
        if (!invoice) return;

        Swal.fire({
            title: 'Email Invoice',
            input: 'email',
            inputLabel: 'Send invoice to:',
            inputValue: invoice.clinicId?.email || '',
            inputAttributes: {
                autocapitalize: 'off',
                style: 'color: #000 !important; font-weight: 500; background-color: #fff; opacity: 1;'
            },
            customClass: {
                input: 'text-dark fw-medium'
            },
            showCancelButton: true,
            confirmButtonText: 'Send',
            showLoaderOnConfirm: true,
            preConfirm: async (email) => {
                if (!email) Swal.showValidationMessage('Please enter an email address');
                try {
                    await api.post(`/api/admin/invoices/${invoiceId}/email`, { targetEmail: email });
                    return true;
                } catch (error) {
                    Swal.showValidationMessage(error.response?.data?.message || 'Failed to send email');
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sent!',
                    text: 'Invoice has been emailed successfully.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        });
    };

    const handleDownloadInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setShowInvoiceModal(true);
    };







    const fetchAuditLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (auditStartDate) params.append('startDate', auditStartDate);
            if (auditEndDate) params.append('endDate', auditEndDate);

            const response = await api.get(`/api/admin/audit-logs?${params.toString()}`);
            setAuditLogs(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            setLoading(false);
        }
    }, [auditStartDate, auditEndDate]);

    const fetchClinics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/clinics');
            setClinics(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching clinics:', error);
            setLoading(false);
        }
    }, []);

    const fetchUserProfile = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/users/me');
            // setCurrentUser(response.data); // Removed unused state
            setProfileForm(prev => ({
                ...prev,
                name: response.data.name,
                email: response.data.email,
                phone: response.data.phone || ''
            }));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching profile:', error);
            setLoading(false);
        }
    }, [setProfileForm]);

    // --- Clinic Modal Functions ---


    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/users', {
                params: { role: userFilter, search: userSearch }
            });
            setUsers(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            setLoading(false);
        }
    }, [userFilter, userSearch]);

    // Refetch users when filter/search changes
    useEffect(() => {
        if (view === 'users') {
            fetchUsers();
        }
    }, [userFilter, userSearch, view, fetchUsers]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const currentView = queryParams.get('view');
        if (currentView === 'settings') {
            setView('settings');
            fetchUserProfile();
        } else if (currentView === 'audit-logs') {
            setView('audit-logs');
            fetchAuditLogs();
        } else if (currentView === 'clinics') {
            setView('clinics');
            fetchClinics();
        } else if (currentView === 'users') {
            setView('users');
            fetchUsers();
        } else if (currentView === 'plans') { // New: Plans View
            setView('plans');
            fetchPlans();
        } else if (currentView === 'billing') { // New: Billing View
            setView('billing');
            fetchInvoices();
        } else if (currentView === 'support') { // New: Support View
            setView('support');
            fetchTickets();
        } else {
            setView('analytics');
            fetchAnalytics();
            fetchAuditLogs(); // Fetch logs for Recent Activities
        }
    }, [location, fetchAnalytics, fetchAuditLogs, fetchClinics, fetchInvoices, fetchPlans, fetchTickets, fetchUsers, fetchUserProfile]);

    useEffect(() => {
        if (selectedTicket) {
            setTicketStatus(selectedTicket.status);
        }
    }, [selectedTicket]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const openViewModal = (clinic) => {
        setViewClinicData(clinic);
        setShowViewModal(true);
    };

    const openCreateModal = () => {
        setIsEditing(false);
        setCurrentClinicId(null);
        setFormData({
            clinicName: '', address: '', contactNumber: '', emergencyContactNumber: '', mapUrl: '',
            subscriptionPlan: '', status: 'Active',
            adminName: '', adminEmail: '', adminPassword: '', adminPhone: ''
        });
        setOnboardingStep(1);
        setSelectedPlanId(null);
        setEmailStatus('idle');
        fetchPlans(); // Fetch plans when opening the modal
        setShowModal(true);
    };

    const openEditModal = (clinic) => {
        setIsEditing(true);
        setCurrentClinicId(clinic._id);
        setFormData({
            clinicName: clinic.name,
            address: clinic.address,
            contactNumber: clinic.contactNumber,
            emergencyContactNumber: clinic.emergencyContactNumber || '',
            mapUrl: clinic.mapUrl || '',
            subscriptionPlan: clinic.subscriptionPlan || 'Standard',
            status: clinic.status || 'Active',
            adminName: clinic.adminId?.name || '',
            adminEmail: clinic.adminId?.email || '',
            adminPhone: clinic.adminId?.phone || ''
        });
        setShowModal(true);
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();

        try {
            if (isEditing) {
                // UPDATE Logic (Existing)
                const updateData = {
                    name: formData.clinicName,
                    address: formData.address,
                    contactNumber: formData.contactNumber,
                    emergencyContactNumber: formData.emergencyContactNumber,
                    mapUrl: formData.mapUrl,
                    subscriptionPlan: formData.subscriptionPlan,
                    status: formData.status,
                    adminName: formData.adminName,
                    adminEmail: formData.adminEmail,
                    adminPhone: formData.adminPhone
                };

                await api.put(`/api/admin/clinics/${currentClinicId}`, updateData);
                Swal.fire('Success', 'Clinic updated successfully', 'success');
                setShowModal(false);
                fetchClinics();

            } else {
                // INVITE Logic (Wizard)
                if (onboardingStep === 1) {
                    // Validate Step 1 and Move to Step 2
                    if (!formData.clinicName || !formData.address || !formData.adminEmail || !formData.adminPassword) {
                        Swal.fire('Error', 'Please fill in all required fields', 'error');
                        return;
                    }
                    if (emailStatus === 'taken') {
                        Swal.fire('Error', 'Admin email is already registered', 'error');
                        return;
                    }
                    setOnboardingStep(2);
                } else if (onboardingStep === 2) {
                    // Submit Invitation
                    if (!selectedPlanId) {
                        Swal.fire('Error', 'Please select a subscription plan', 'error');
                        return;
                    }

                    const inviteData = {
                        name: formData.clinicName,
                        address: formData.address,
                        adminName: formData.adminName,
                        adminEmail: formData.adminEmail,
                        adminPassword: formData.adminPassword,
                        adminPhone: formData.adminPhone, // Included
                        planId: selectedPlanId
                    };

                    await api.post('/api/admin/invite-clinic', inviteData);

                    Swal.fire({
                        icon: 'success',
                        title: 'Invitation Sent!',
                        text: `An invitation has been sent to ${formData.adminEmail}`,
                        timer: 3000,
                        showConfirmButton: false
                    });

                    setShowModal(false);
                    fetchClinics();
                }
            }
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
        }
    };

    const handleDeleteClinic = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will delete the clinic and its admin permanently!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/api/admin/clinics/${id}`);
                Swal.fire('Deleted!', 'Clinic has been deleted.', 'success');
                fetchClinics();
            } catch (error) {
                Swal.fire('Error', 'Failed to delete clinic', 'error');
            }
        }
    };

    // --- Profile Settings Functions ---
    // const openPlanModal = (plan = null) => {
    //     if (plan) {
    //         setEditingPlanId(plan._id);
    //         setPlanFormData({ ...plan });
    //     } else {
    //         setEditingPlanId(null);
    //         setPlanFormData({
    //             name: '', price: '', currency: 'INR', duration: 'monthly',
    //             maxDoctors: '', maxStaff: '', storageGB: '', features: ''
    //         });
    //     }
    //     setShowPlanModal(true);
    // };

    const handleProfileChange = (e) => {
        setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();

        if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmNewPassword) {
            Swal.fire('Error', 'New passwords do not match', 'error');
            return;
        }


        try {
            const token = localStorage.getItem('token');
            const updateData = {
                name: profileForm.name,
                phone: profileForm.phone
            };
            if (profileForm.newPassword) {
                updateData.currentPassword = profileForm.currentPassword;
                updateData.newPassword = profileForm.newPassword;
            }

            const response = await api.put('/api/users/profile', updateData);

            Swal.fire('Success', response.data.message, 'success');
            setProfileForm(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            }));
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to update profile', 'error');
        }
    };

    const handlePlanSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: planFormData.name,
                price: parseFloat(planFormData.price),
                currency: planFormData.currency,
                duration: planFormData.duration,
                features: typeof planFormData.features === 'string' ? planFormData.features.split(',').map(f => f.trim()).filter(f => f) : planFormData.features,
                limits: {
                    maxDoctors: parseInt(planFormData.maxDoctors) || 0,
                    maxStaff: parseInt(planFormData.maxStaff) || 0,
                    storageGB: parseInt(planFormData.storageGB) || 0
                },
                isActive: planFormData.isActive !== undefined ? planFormData.isActive : true
            };

            if (editingPlanId) {
                await api.put(`/api/admin/plans/${editingPlanId}`, payload);
                Swal.fire('Success', 'Plan updated successfully', 'success');
            } else {
                await api.post('/api/admin/plans', payload);
                Swal.fire('Success', 'Plan created successfully', 'success');
            }
            setShowPlanModal(false);
            fetchPlans();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
        }
    };

    const handleTicketSubmit = async (e) => {
        e.preventDefault();
        if (!replyMessage.trim()) return;
        try {
            const response = await api.put(`/api/admin/tickets/${selectedTicket._id}`, {
                status: ticketStatus,
                message: replyMessage
            });

            console.log('Server Reply Response:', response.data);

            // Validate essential fields before updating state (prevent UI disappearance)
            if (!response.data || !response.data.clinicId || !response.data.userId) {
                console.error('CRITICAL: Server returned incomplete ticket data! State update aborted to prevent crash.', response.data);
                Swal.fire('Warning', 'Message sent, but display update failed. Please reload.', 'warning');
                return;
            }

            // Update the selected ticket with the new data from server (including populated responder)
            setSelectedTicket(response.data);
            setReplyMessage('');

            // Update the tickets list in the background
            setTickets(prev => prev.map(t => t._id === response.data._id ? response.data : t));

            // Auto scroll to bottom
            setTimeout(() => {
                if (chatEndRef.current) {
                    chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);

        } catch (error) {
            Swal.fire('Error', 'Failed to send message', 'error');
        }
    };

    const handleQuickStatusUpdate = async (ticketId, newStatus) => {
        try {
            await api.put(`/api/admin/tickets/${ticketId}`, {
                status: newStatus
            });
            fetchTickets();
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            Toast.fire({
                icon: 'success',
                title: `Status updated to ${newStatus}`
            });
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };



    return (
        <section className={mobileMenu ? "overlay active" : "overlay"}>
            <Sidebar
                isSidebarActive={sidebarActive}
                mobileMenu={mobileMenu}
                closeSidebar={closeSidebar}
            />

            <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
                <Header
                    toggleSidebar={toggleSidebar}
                    mobileMenuControl={mobileMenuControl}
                />

                <div className="dashboard-main-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                        <h6 className="fw-semibold mb-0">Super Admin Dashboard</h6>
                        <ul className="d-flex align-items-center gap-2">
                            <li className="fw-medium">
                                <a href="/" className="d-flex align-items-center gap-1 hover-text-primary">
                                    <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                                    Dashboard
                                </a>
                            </li>
                            <li>-</li>
                            <li className="fw-medium text-capitalize">{view.replace('-', ' ')}</li>
                        </ul>
                    </div>

                    {view === 'analytics' && (
                        <div>
                            <div className="d-flex justify-content-end mb-24">
                                <button
                                    onClick={openCreateModal}
                                    className="btn btn-primary text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2"
                                >
                                    <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                                    Add Clinic
                                </button>
                            </div>


                            {/* Stats Cards */}
                            <div className="row mb-24">
                                {/* 1. Total Clinics */}
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-primary-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-primary-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:hospital-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Total Clinics</span>
                                                <h4 className="mb-0 fw-bold">{analyticsData?.tenantMetrics?.active || 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. Active Clinics
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-success-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-success-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:check-circle-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Active Clinics</span>
                                                <h4 className="mb-0 fw-bold">{analyticsData?.tenantMetrics?.active || 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}

                                {/* 3. Total Doctors */}
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-info-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-info-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:stethoscope-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Total Doctors</span>
                                                <h4 className="mb-0 fw-bold">{analyticsData?.totalDoctors || 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 4. Total Users */}
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-success-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-success-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:users-group-rounded-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Total Users</span>
                                                <h4 className="mb-0 fw-bold">{analyticsData?.totalUsers || 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 5. Total Revenue */}
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-warning-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-warning-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:wallet-money-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Total Revenue (MRR)</span>
                                                <h4 className="mb-0 fw-bold">₹{analyticsData?.revenue ? (analyticsData.revenue.total || 0).toLocaleString() : 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 6. Pending Tickets */}
                                <div className="col-xxl-3 col-md-6 mb-24">
                                    <div className="card h-100 p-24 bg-danger-50 radius-12 border-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="w-50-px h-50-px radius-12 bg-danger-600 text-white d-flex justify-content-center align-items-center text-2xl">
                                                <Icon icon="solar:ticket-bold" />
                                            </div>
                                            <div>
                                                <span className="text-secondary-light text-sm mb-1">Pending Tickets</span>
                                                <h4 className="mb-0 fw-bold">{analyticsData?.pendingTickets || 0}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card h-100 p-0 radius-12 mb-24">
                                <div className="card-header border-bottom bg-base py-16 px-24">
                                    <h6 className="mb-0 fw-bold text-lg text-primary-dark">Recent Activities</h6>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Action</th>
                                                    <th scope="col">Details</th>
                                                    <th scope="col">Performed By</th>
                                                    <th scope="col" className="text-end">Timestamp</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-5">
                                                            <Spinner animation="border" variant="primary" />
                                                        </td>
                                                    </tr>
                                                ) : auditLogs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-5 text-secondary">
                                                            No recent activities.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    auditLogs.slice(0, 5).map((log, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <span className="fw-medium text-primary-dark">{log.action}</span>
                                                            </td>
                                                            <td className="text-secondary text-sm">
                                                                <span className="d-inline-block text-truncate" style={{ maxWidth: '300px' }}>
                                                                    {JSON.stringify(log.details)}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="w-24-px h-24-px rounded-circle bg-info-100 text-info-600 d-flex justify-content-center align-items-center text-xs fw-bold">
                                                                        {log.performedBy.name.charAt(0)}
                                                                    </div>
                                                                    <span className="text-sm fw-medium">{log.performedBy.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end text-secondary text-xs">
                                                                {new Date(log.timestamp).toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                    }

                    {
                        view === 'plans' && (
                            <div className="card h-100 p-0 radius-12 shadow-sm border-0">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                                    <h6 className="mb-0 fw-bold text-lg text-primary-dark">Subscription Plans</h6>
                                    <button
                                        onClick={() => {
                                            setEditingPlanId(null);
                                            setPlanFormData({
                                                name: '', price: '', currency: 'INR', duration: 'monthly',
                                                maxDoctors: '', maxStaff: '', storageGB: '', features: ''
                                            });
                                            setShowPlanModal(true);
                                        }}
                                        className="btn btn-primary text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2"
                                    >
                                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                                        Create Plan
                                    </button>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Plan Name</th>
                                                    <th scope="col">Price</th>
                                                    <th scope="col">Duration</th>
                                                    <th scope="col">Limits</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col" className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="6" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                                                ) : plans.length === 0 ? (
                                                    <tr><td colSpan="6" className="text-center py-5 text-secondary">No plans defined.</td></tr>
                                                ) : (
                                                    plans.map((plan) => (
                                                        <tr key={plan._id}>
                                                            <td><span className="fw-medium text-primary-dark">{plan.name}</span></td>
                                                            <td>{plan.currency === 'INR' ? '₹' : '$'} {plan.price}</td>
                                                            <td className="text-capitalize">{plan.duration}</td>
                                                            <td>
                                                                <div className="d-flex flex-column text-xs text-secondary">
                                                                    <span>Doctors: {plan.limits?.maxDoctors === 0 ? 'Unlimited' : plan.limits?.maxDoctors}</span>
                                                                    <span>Staff: {plan.limits?.maxStaff === 0 ? 'Unlimited' : plan.limits?.maxStaff}</span>
                                                                    <span>Storage: {plan.limits?.storageGB}GB</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${plan.isActive ? 'bg-success-focus text-success-600' : 'bg-danger-focus text-danger-600'}`}>
                                                                    {plan.isActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center gap-2 justify-content-center">
                                                                    {/* VIEW Action */}
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedPlanView(plan);
                                                                            setShowPlanViewModal(true);
                                                                        }}
                                                                        className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle transition-all"
                                                                        title="View Details"
                                                                    >
                                                                        <Icon icon="lucide:eye" className="text-lg" />
                                                                    </button>

                                                                    {/* EDIT Action */}
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingPlanId(plan._id);
                                                                            setPlanFormData({
                                                                                name: plan.name,
                                                                                price: plan.price,
                                                                                currency: plan.currency,
                                                                                duration: plan.duration,
                                                                                maxDoctors: plan.limits?.maxDoctors,
                                                                                maxStaff: plan.limits?.maxStaff,
                                                                                storageGB: plan.limits?.storageGB,
                                                                                features: plan.features.join(', '),
                                                                                isActive: plan.isActive
                                                                            });
                                                                            setShowPlanModal(true);
                                                                        }}
                                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle transition-all"
                                                                        title="Edit Plan"
                                                                    >
                                                                        <Icon icon="lucide:edit" className="text-lg" />
                                                                    </button>

                                                                    {/* DELETE Action */}
                                                                    <button
                                                                        onClick={async () => {
                                                                            const result = await Swal.fire({
                                                                                title: 'Delete Plan?',
                                                                                text: 'Clinics on this plan might be affected!',
                                                                                icon: 'warning',
                                                                                showCancelButton: true,
                                                                                confirmButtonColor: '#d33',
                                                                                confirmButtonText: 'Yes, delete it!'
                                                                            });
                                                                            if (result.isConfirmed) {
                                                                                try {
                                                                                    await api.delete(`/api/admin/plans/${plan._id}`);
                                                                                    Swal.fire('Deleted!', 'Plan has been deleted.', 'success');
                                                                                    fetchPlans();
                                                                                } catch (e) {
                                                                                    Swal.fire('Error', e.response?.data?.message || 'Failed to delete plan', 'error');
                                                                                }
                                                                            }
                                                                        }}
                                                                        className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle transition-all"
                                                                        title="Delete Plan"
                                                                    >
                                                                        <Icon icon="fluent:delete-24-regular" className="text-lg" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                    }



                    {
                        view === 'support' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24">
                                    <h6 className="mb-0 fw-bold text-lg text-primary-dark">Support Tickets</h6>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Subject</th>
                                                    <th scope="col">User</th>
                                                    <th scope="col">Clinic</th>
                                                    <th scope="col">Priority</th>
                                                    <th scope="col" className="text-center">Status</th>
                                                    <th scope="col" className="text-end">Last Update</th>
                                                    <th scope="col" className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                                                ) : tickets.length === 0 ? (
                                                    <tr><td colSpan="7" className="text-center py-5 text-secondary">No tickets found.</td></tr>
                                                ) : (
                                                    tickets.map((ticket) => (
                                                        <tr key={ticket._id}>
                                                            <td><span className="fw-medium text-primary-dark">{ticket.subject}</span></td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-sm fw-medium">{ticket.userId?.name || 'Unknown'}</span>
                                                                    <span className="text-xs text-secondary">{ticket.userId?.email}</span>
                                                                </div>
                                                            </td>
                                                            <td>{ticket.clinicId?.name || 'N/A'}</td>
                                                            <td>
                                                                <span className={`badge ${ticket.priority === 'High' ? 'bg-danger-focus text-danger-600' : ticket.priority === 'Medium' ? 'bg-warning-focus text-warning-600' : 'bg-info-focus text-info-600'}`}>
                                                                    {ticket.priority}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                {ticket.status === 'Closed' ? (
                                                                    <span className="badge bg-secondary text-white border-0 px-3 py-2">Ticket Closed</span>
                                                                ) : (
                                                                    <Form.Select
                                                                        size="sm"
                                                                        className={`badge border-0 ${ticket.status === 'Open' ? 'bg-primary-focus text-primary-600' : ticket.status === 'Resolved' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600'}`}
                                                                        value={ticket.status}
                                                                        onChange={(e) => handleQuickStatusUpdate(ticket._id, e.target.value)}
                                                                    >
                                                                        <option value="Open">Open</option>
                                                                        <option value="In Progress">In Progress</option>
                                                                        <option value="Resolved">Resolved</option>
                                                                    </Form.Select>
                                                                )}
                                                            </td>
                                                            <td className="text-end text-secondary text-xs">{new Date(ticket.updatedAt || ticket.createdAt).toLocaleString()}</td>
                                                            <td className="text-center">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedTicket(ticket);
                                                                        setTicketStatus(ticket.status);
                                                                        setReplyMessage('');
                                                                        setShowTicketModal(true);
                                                                    }}
                                                                    className="btn btn-primary text-xs px-2 py-1 radius-4"
                                                                >
                                                                    Manage
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {

                    }

                    {
                        view === 'billing' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                                    <h5 className="mb-0">Billing & Invoices</h5>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search Clinic/Inv#"
                                                value={invoiceSearch}
                                                onChange={(e) => setInvoiceSearch(e.target.value)}
                                                style={{ maxWidth: '180px' }}
                                                className="form-control-sm"
                                            />
                                            <Form.Control
                                                type="date"
                                                value={invoiceStartDate}
                                                onChange={(e) => setInvoiceStartDate(e.target.value)}
                                                className="form-control-sm"
                                            />
                                            <span className="text-muted">-</span>
                                            <Form.Control
                                                type="date"
                                                value={invoiceEndDate}
                                                onChange={(e) => setInvoiceEndDate(e.target.value)}
                                                className="form-control-sm"
                                            />
                                            <button className="btn btn-primary btn-sm" onClick={handleApplyInvoiceFilters} title="Apply Filter">
                                                <Icon icon="solar:filter-bold" />
                                            </button>
                                            <button className="btn btn-outline-secondary btn-sm" onClick={handleClearInvoiceFilters} title="Clear">
                                                <Icon icon="solar:restart-bold" />
                                            </button>
                                        </div>

                                    </div>
                                </div>
                                <div className="card-body p-24">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 text-secondary">Loading invoices...</p>
                                        </div>
                                    ) : invoices.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="bg-primary-50 d-inline-flex p-4 rounded-circle mb-3 text-primary-300">
                                                <Icon icon="solar:bill-list-bold" width="48" height="48" />
                                            </div>
                                            <h5 className="text-secondary fw-bold">No Invoices Found</h5>
                                        </div>
                                    ) : (
                                        <div className="table-responsive scroll-sm">
                                            <table className="table bordered-table sm-table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Invoice #</th>
                                                        <th scope="col">Clinic</th>
                                                        <th scope="col">Plan</th>
                                                        <th scope="col">Amount</th>
                                                        <th scope="col">Date</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col" className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoices.map((inv) => (
                                                        <tr key={inv._id}>
                                                            <td><span className="fw-medium text-primary-dark">{inv.invoiceNumber}</span></td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-medium text-dark">{inv.clinicId?.name || 'Unknown'}</span>
                                                                    <span className="text-xs text-secondary">{inv.clinicId?.email}</span>
                                                                </div>
                                                            </td>
                                                            <td><span className="badge bg-info-focus text-info-600">{inv.subscriptionPlan}</span></td>
                                                            <td className="fw-bold">{inv.currency} {inv.amount}</td>
                                                            <td className="text-secondary text-sm">{new Date(inv.paidAt).toLocaleDateString()}</td>
                                                            <td>
                                                                <span className="badge bg-success-focus text-success-600 border border-success-main">
                                                                    {inv.status}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                                    <button
                                                                        onClick={() => handleDownloadInvoice(inv)}
                                                                        className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle transition-all"
                                                                        title="Download"
                                                                    >
                                                                        <Icon icon="solar:download-bold" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEmailInvoice(inv._id)}
                                                                        className="bg-secondary-50 text-secondary-600 bg-hover-secondary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle transition-all"
                                                                        title="Email to Clinic"
                                                                    >
                                                                        <Icon icon="solar:letter-bold" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }
                    {
                        view === 'audit-logs' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                                    <h6 className="mb-0 fw-bold text-lg text-primary-dark">System Audit Log</h6>
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Control
                                            type="date"
                                            value={auditStartDate}
                                            onChange={(e) => setAuditStartDate(e.target.value)}
                                            className="form-control-sm"
                                        />
                                        <span className="text-muted">-</span>
                                        <Form.Control
                                            type="date"
                                            value={auditEndDate}
                                            onChange={(e) => setAuditEndDate(e.target.value)}
                                            className="form-control-sm"
                                        />
                                        <button className="btn btn-primary btn-sm" onClick={fetchAuditLogs} title="Apply Filter">
                                            <Icon icon="solar:filter-bold" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => { setAuditStartDate(''); setAuditEndDate(''); fetchAuditLogs(); }}
                                            title="Clear"
                                        >
                                            <Icon icon="solar:restart-bold" />
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Action</th>
                                                    <th scope="col">Details</th>
                                                    <th scope="col">Performed By</th>
                                                    <th scope="col" className="text-end">Timestamp</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-5">
                                                            <Spinner animation="border" variant="primary" />
                                                            <p className="mt-2 text-secondary text-sm">Loading audit logs...</p>
                                                        </td>
                                                    </tr>
                                                ) : auditLogs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-5 text-secondary">
                                                            No system logs found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    auditLogs.map((log, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <span className="fw-medium text-primary-dark">{log.action}</span>
                                                            </td>
                                                            <td className="text-secondary text-sm">
                                                                {JSON.stringify(log.details)}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="w-24-px h-24-px rounded-circle bg-info-100 text-info-600 d-flex justify-content-center align-items-center text-xs fw-bold">
                                                                        {log.performedBy.name.charAt(0)}
                                                                    </div>
                                                                    <span className="text-sm fw-medium">{log.performedBy.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-end text-secondary text-xs">
                                                                {new Date(log.timestamp).toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {
                        view === 'users' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                                    <h5 className="mb-0">User Management</h5>
                                    <div className="d-flex align-items-center gap-3">
                                        <Form.Select
                                            className="w-auto border-0 bg-white shadow-sm radius-8 text-secondary fw-medium"
                                            value={userFilter}
                                            onChange={(e) => setUserFilter(e.target.value)}
                                            style={{ minWidth: '150px' }}
                                        >
                                            <option value="All">All Roles</option>
                                            <option value="Doctor">Doctors</option>
                                            <option value="Patient">Patients</option>
                                            <option value="ClinicAdmin">Clinic Admins</option>
                                            <option value="Assistant">Assistants</option>
                                        </Form.Select>
                                        <div>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search users..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                className="border-0 shadow-sm radius-8"
                                                style={{ minWidth: '250px' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body p-24">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2 text-secondary">Loading users...</p>
                                        </div>
                                    ) : users.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="bg-primary-50 d-inline-flex p-4 rounded-circle mb-3 text-primary-300">
                                                <Icon icon="solar:users-group-rounded-bold" width="48" height="48" />
                                            </div>
                                            <h5 className="text-secondary fw-bold">No Users Found</h5>
                                            <p className="text-secondary-light">Try adjusting your filters or search query.</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive scroll-sm">
                                            <table className="table bordered-table sm-table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">User</th>
                                                        <th scope="col">Role</th>
                                                        <th scope="col">Contact</th>
                                                        <th scope="col">Joined</th>
                                                        <th scope="col" className="text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map((user) => (
                                                        <tr key={user._id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div className="w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-lg fw-bold flex-shrink-0">
                                                                        <Icon icon="solar:user-bold" />
                                                                    </div>
                                                                    <div>
                                                                        <h6 className="mb-0 fw-medium text-dark text-md">{user.name}</h6>
                                                                        <span className="text-xs text-secondary">{user._id.substring(0, 8)}...</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={`px-24 py-4 radius-4 fw-medium text-sm ${user.role === 'Doctor' ? 'bg-info-focus text-info-600 border border-info-main' :
                                                                    user.role === 'Patient' ? 'bg-success-focus text-success-600 border border-success-main' :
                                                                        user.role === 'ClinicAdmin' ? 'bg-primary-focus text-primary-600 border border-primary-main' :
                                                                            'bg-warning-focus text-warning-600 border border-warning-main'
                                                                    }`}>
                                                                    {user.role}
                                                                </span>
                                                                {user.clinicId && (
                                                                    <div className="text-xs text-secondary mt-2 fw-medium d-flex align-items-center gap-1">
                                                                        <Icon icon="solar:hospital-line-duotone" />
                                                                        {user.clinicId.name}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span className="text-sm text-dark fw-medium">{user.email}</span>
                                                                    <span className="text-xs text-secondary">{user.phone || 'N/A'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-secondary text-sm">
                                                                {new Date(user.createdAt).toLocaleDateString()}
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    onClick={() => { setSelectedUser(user); setShowUserModal(true); }}
                                                                    className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle mx-auto transition-all"
                                                                >
                                                                    <Icon icon="lucide:eye" className="text-xl" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    } { /* End Users Section */}


                    {
                        view === 'settings' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24">
                                    <h5 className="mb-0">Profile Settings</h5>
                                </div>
                                <div className="card-body p-24">
                                    <Form onSubmit={handleProfileUpdate}>
                                        <Row className="mb-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Full Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="name"
                                                        value={profileForm.name}
                                                        onChange={handleProfileChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Email Address</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        value={profileForm.email}
                                                        disabled
                                                        className="bg-light"
                                                    />
                                                    <Form.Text className="text-muted">Email cannot be changed.</Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-4">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Phone Number</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="phone"
                                                        value={profileForm.phone}
                                                        onChange={handleProfileChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <hr className="my-4" />
                                        <h6 className="text-primary-600 mb-3">Change Password</h6>

                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Current Password</Form.Label>
                                                    <Form.Control
                                                        type="password"
                                                        name="currentPassword"
                                                        value={profileForm.currentPassword}
                                                        onChange={handleProfileChange}
                                                        placeholder="Required to set new password"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>New Password</Form.Label>
                                                    <Form.Control
                                                        type="password"
                                                        name="newPassword"
                                                        value={profileForm.newPassword}
                                                        onChange={handleProfileChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Confirm New Password</Form.Label>
                                                    <Form.Control
                                                        type="password"
                                                        name="confirmNewPassword"
                                                        value={profileForm.confirmNewPassword}
                                                        onChange={handleProfileChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end mt-4">
                                            <Button variant="primary" type="submit">
                                                Save Changes
                                            </Button>
                                        </div>
                                    </Form>
                                </div>
                            </div>
                        )
                    }

                    {
                        view === 'clinics' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                                    <h5 className="mb-0">Registered Clinics</h5>
                                    <div className="d-flex align-items-center gap-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search Clinics..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="form-control form-control-sm w-auto"
                                            style={{ minWidth: '200px' }}
                                        />
                                        <button
                                            onClick={openCreateModal}
                                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                                        >
                                            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                                            Add Clinic
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Clinic Name</th>
                                                    <th scope="col">Address</th>
                                                    <th scope="col">Plan</th>
                                                    <th scope="col">Admin</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col" className="text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading && clinics.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-5">
                                                            <Spinner animation="border" variant="primary" />
                                                            <p className="mt-2 text-secondary text-sm">Loading clinics...</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    (() => {
                                                        const filteredClinics = clinics.filter(clinic =>
                                                            clinic.name.toLowerCase().includes(searchQuery.toLowerCase())
                                                        );

                                                        if (filteredClinics.length === 0) {
                                                            return (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center">
                                                                        No clinics found matching "{searchQuery}"
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        return filteredClinics.map((clinic) => (
                                                            <tr key={clinic._id}>
                                                                <td>
                                                                    <span className="fw-medium text-primary-dark">{clinic.name}</span>
                                                                </td>
                                                                <td className="text-secondary text-sm">{clinic.address}</td>
                                                                <td>
                                                                    <span className={`px-8 py-2 radius-4 text-xs fw-medium ${clinic.subscriptionPlan === 'Enterprise' ? 'bg-success-focus text-success-600' : 'bg-info-focus text-info-600'}`}>
                                                                        {clinic.subscriptionPlan}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex flex-column">
                                                                        <span className="text-sm fw-medium">{clinic.adminId?.name || 'N/A'}</span>
                                                                        <span className="text-xs text-secondary-light">{clinic.adminId?.email}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{clinic.adminId?.phone || 'N/A'}</td>
                                                                <td className="text-center">
                                                                    <div className="d-flex align-items-center gap-10 justify-content-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openViewModal(clinic)}
                                                                            className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                        >
                                                                            <Icon icon="lucide:eye" className="menu-icon" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openEditModal(clinic)}
                                                                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                        >
                                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteClinic(clinic._id)}
                                                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                        >
                                                                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ));
                                                    })()
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )
                    }

                    {/* Unified Create/Edit Clinic Modal */}
                    <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>{isEditing ? 'Edit Clinic' : 'Create New Clinic'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {isEditing ? (
                                <Form onSubmit={handleModalSubmit}>
                                    <h6 className="text-primary-600 mb-3">Clinic Information</h6>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Clinic Name</Form.Label>
                                                <Form.Control type="text" name="clinicName" value={formData.clinicName} onChange={handleInputChange} required />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Address</Form.Label>
                                                <Form.Control type="text" name="address" value={formData.address} onChange={handleInputChange} required />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Subscription Plan</Form.Label>
                                                <Form.Select name="subscriptionPlan" value={formData.subscriptionPlan} onChange={handleInputChange}>
                                                    {plans.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                                                    <option value="Basic">Basic</option>
                                                    <option value="Premium">Premium</option>
                                                    <option value="Enterprise">Enterprise</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select name="status" value={formData.status} onChange={handleInputChange}>
                                                    <option value="Active">Active</option>
                                                    <option value="Suspended">Suspended</option>
                                                    <option value="Inactive">Inactive</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <hr className="my-4" />
                                    <h6 className="text-primary-600 mb-3">Administrator Details</h6>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Admin Name</Form.Label>
                                                <Form.Control type="text" name="adminName" value={formData.adminName} onChange={handleInputChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Admin Phone</Form.Label>
                                                <Form.Control type="text" name="adminPhone" value={formData.adminPhone} onChange={handleInputChange} />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Admin Email (Cannot be changed)</Form.Label>
                                                <Form.Control type="email" value={formData.adminEmail} disabled className="bg-light" />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                                        <Button variant="primary" type="submit">Update Clinic</Button>
                                    </div>
                                </Form>
                            ) : (
                                <Form onSubmit={handleModalSubmit}>

                                    {onboardingStep === 1 && (
                                        <div className="animate-fade-in">
                                            <div className="text-center mb-24">
                                                <div className="d-flex justify-content-center gap-3 mb-16">
                                                    <span className="badge bg-primary-600 text-white p-3 rounded-circle w-40-px h-40-px d-flex justify-content-center align-items-center fs-5 shadow-sm border border-white">1</span>
                                                    <span className="badge bg-neutral-100 text-secondary-light p-3 rounded-circle w-40-px h-40-px d-flex justify-content-center align-items-center fs-5 shadow-inner">2</span>
                                                </div>
                                                <h5 className="fw-bold text-dark">Clinic & Admin Details</h5>
                                                <p className="text-secondary text-sm">Enter the initial setup information for the new clinic.</p>
                                            </div>

                                            <div className="row g-4 mb-24">
                                                <h6 className="text-primary-dark mb-0 border-start border-primary-500 ps-3">Clinic Information</h6>
                                                <Col md={12}>
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Clinic Name <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            className="form-control-lg shadow-sm border-neutral-200"
                                                            type="text"
                                                            name="clinicName"
                                                            value={formData.clinicName}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="e.g. City Care Clinic"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Address <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            className="form-control-lg shadow-sm border-neutral-200"
                                                            type="text"
                                                            name="address"
                                                            value={formData.address}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="Full Postal Address"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </div>

                                            <div className="row g-4 mb-24">
                                                <h6 className="text-primary-dark mb-0 border-start border-primary-500 ps-3">Administrator</h6>
                                                <Col md={6}>
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Admin Name</Form.Label>
                                                        <Form.Control
                                                            className="shadow-sm border-neutral-200"
                                                            type="text"
                                                            name="adminName"
                                                            value={formData.adminName}
                                                            onChange={handleInputChange}
                                                            placeholder="Full Name"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-0 position-relative">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Email <span className="text-danger">*</span></Form.Label>
                                                        <div className="position-relative">
                                                            <Form.Control
                                                                className={`shadow-sm border-neutral-200 ${emailStatus === 'taken' ? 'is-invalid' : emailStatus === 'available' ? 'is-valid' : ''}`}
                                                                type="email"
                                                                name="adminEmail"
                                                                value={formData.adminEmail}
                                                                onChange={(e) => {
                                                                    handleInputChange(e);
                                                                    if (emailStatus !== 'idle') setEmailStatus('idle');
                                                                }}
                                                                onBlur={(e) => checkEmailAvailability(e.target.value)}
                                                                required
                                                                placeholder="admin@clinic.com"
                                                            />
                                                            {emailStatus === 'checking' && (
                                                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                                                    <Spinner animation="border" size="sm" variant="primary" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        {emailStatus === 'taken' && <Form.Text className="text-danger">Email already registered.</Form.Text>}
                                                        {emailStatus === 'available' && <Form.Text className="text-success">Email available.</Form.Text>}
                                                    </Form.Group>
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Admin Password <span className="text-danger">*</span></Form.Label>
                                                        <Form.Control
                                                            className="form-control-lg shadow-sm border-neutral-200"
                                                            type="password"
                                                            name="adminPassword"
                                                            value={formData.adminPassword}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="Create a strong password"
                                                            minLength={6}
                                                        />
                                                        <Form.Text className="text-muted text-xs">Min. 6 characters.</Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Group className="mb-0">
                                                        <Form.Label className="fw-semibold text-secondary-dark">Admin Mobile Number</Form.Label>
                                                        <Form.Control
                                                            className="form-control-lg shadow-sm border-neutral-200"
                                                            type="tel"
                                                            name="adminPhone"
                                                            value={formData.adminPhone}
                                                            onChange={handleInputChange}
                                                            placeholder="+91 9876543210"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </div>

                                            <div className="d-flex justify-content-end gap-3 mt-32 border-top pt-24">
                                                <Button variant="outline-secondary" className="px-32 py-2 fw-semibold" onClick={() => setShowModal(false)}>Cancel</Button>
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    className="px-32 py-2 fw-semibold shadow-sm"
                                                    disabled={emailStatus === 'taken' || emailStatus === 'checking'}
                                                >
                                                    Next Step <Icon icon="solar:arrow-right-bold" className="ms-2" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {onboardingStep === 2 && (
                                        <div className="animate-fade-in">
                                            <div className="text-center mb-24">
                                                <div className="d-flex justify-content-center gap-3 mb-16">
                                                    <span className="badge bg-success-600 text-white p-3 rounded-circle w-40-px h-40-px d-flex justify-content-center align-items-center fs-5 shadow-sm check-icon"><Icon icon="solar:check-read-bold" /></span>
                                                    <span className="badge bg-primary-600 text-white p-3 rounded-circle w-40-px h-40-px d-flex justify-content-center align-items-center fs-5 shadow-sm border border-white">2</span>
                                                </div>
                                                <h5 className="fw-bold text-dark">Select Subscription Plan</h5>
                                                <p className="text-secondary text-sm">Choose a plan to invite the clinic with. They will complete payment.</p>
                                            </div>

                                            <div className="row g-4 plan-selection-grid text-start">
                                                {plans.map((plan) => (
                                                    <Col md={6} lg={4} key={plan._id}>
                                                        <PlanDetailCard
                                                            plan={plan}
                                                            onSelect={setSelectedPlanId}
                                                            isSelected={selectedPlanId === plan._id}
                                                            showSelectButton={true}
                                                        />
                                                    </Col>
                                                ))}
                                                {plans.length === 0 && <p className="text-center text-muted">No plans available.</p>}
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center mt-32 border-top pt-24">
                                                <Button variant="outline-secondary" className="px-32 fw-semibold" onClick={() => setOnboardingStep(1)}>
                                                    <Icon icon="solar:arrow-left-bold" className="me-2" /> Back
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    className="px-32 fw-semibold shadow-sm"
                                                    disabled={!selectedPlanId}
                                                >
                                                    Send Invite <Icon icon="solar:plain-bold" className="ms-2" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </Form>
                            )}
                        </Modal.Body>
                    </Modal>

                    {/* View Clinic Modal */}
                    <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Clinic Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {viewClinicData && (
                                <div className="p-3">
                                    <div className="d-flex align-items-center gap-3 mb-4">
                                        <div className="w-60-px h-60-px radius-12 bg-primary-100 d-flex justify-content-center align-items-center text-primary-600 text-3xl">
                                            <Icon icon="solar:hospital-bold" />
                                        </div>
                                        <div>
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <h5 className="mb-0 fw-bold">{viewClinicData.name}</h5>
                                                <span className={`px-8 py-1 radius-4 text-xs fw-medium ${viewClinicData.status === 'Active' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600'}`}>
                                                    {viewClinicData.status}
                                                </span>
                                            </div>
                                            <span className={`px-12 py-4 radius-4 text-xs fw-medium ${viewClinicData.subscriptionPlan === 'Enterprise' ? 'bg-success-focus text-success-600' : 'bg-info-focus text-info-600'}`}>
                                                {viewClinicData.subscriptionPlan} Plan
                                            </span>
                                        </div>
                                    </div>

                                    <h6 className="text-secondary-light text-uppercase fw-semibold text-sm mb-3">General Information</h6>
                                    <Row className="mb-4">
                                        <Col md={6} className="mb-3">
                                            <label className="fw-medium text-secondary text-sm">Address</label>
                                            <p className="mb-0 fw-medium text-primary-dark">{viewClinicData.address || 'N/A'}</p>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <label className="fw-medium text-secondary text-sm">Contact Number</label>
                                            <p className="mb-0 fw-medium text-primary-dark">{viewClinicData.contactNumber || viewClinicData.adminId?.phone || 'N/A'}</p>
                                        </Col>
                                        <Col md={6} className="mb-3">
                                            <label className="fw-medium text-secondary text-sm">Emergency Contact</label>
                                            <p className="mb-0 fw-medium text-danger-600">{viewClinicData.emergencyContactNumber || 'N/A'}</p>
                                        </Col>
                                        <Col md={12} className="mb-3">
                                            <label className="fw-medium text-secondary text-sm">Map Location</label>
                                            {viewClinicData.mapUrl ? (
                                                <div className="mt-2">
                                                    <a href={viewClinicData.mapUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover-text-primary-800 d-inline-flex align-items-center gap-1">
                                                        <Icon icon="solar:map-arrow-right-bold" /> Open in Google Maps
                                                    </a>
                                                    <div className="mt-2 text-xs text-secondary text-truncate">
                                                        {viewClinicData.mapUrl}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="mb-0 text-secondary">No map URL provided.</p>
                                            )}
                                        </Col>
                                    </Row>

                                    <hr className="my-4 border-dashed" />

                                    <h6 className="text-secondary-light text-uppercase fw-semibold text-sm mb-3">Administrator Details</h6>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <label className="fw-medium text-secondary text-sm">Name</label>
                                            <p className="mb-0 fw-medium text-primary-dark">{viewClinicData.adminId?.name || 'N/A'}</p>
                                        </Col>
                                        <Col md={4}>
                                            <label className="fw-medium text-secondary text-sm">Email</label>
                                            <p className="mb-0 fw-medium text-primary-dark">{viewClinicData.adminId?.email || 'N/A'}</p>
                                        </Col>
                                        <Col md={4}>
                                            <label className="fw-medium text-secondary text-sm">Phone</label>
                                            <p className="mb-0 fw-medium text-primary-dark">{viewClinicData.adminId?.phone || 'N/A'}</p>
                                        </Col>
                                    </Row>

                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                        </Modal.Footer>
                    </Modal>
                </div >

                {/* User Details Modal (Read-Only) */}
                < Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered size="md" className="user-details-modal" >
                    <Modal.Body className="p-0 radius-24 overflow-hidden bg-white">
                        {selectedUser && (
                            <div>
                                {/* Premium Header Background */}
                                <div className="position-relative bg-gradient-to-r from-primary-600 to-primary-700 h-120-px w-100 overflow-hidden">
                                    <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary-600" style={{ opacity: 0.9 }}></div>
                                    {/* Decorative Circles */}
                                    <div className="position-absolute top-0 end-0 bg-white opacity-10 rounded-circle w-150-px h-150-px translate-middle"></div>
                                    <div className="position-absolute bottom-0 start-0 bg-white opacity-10 rounded-circle w-100-px h-100-px translate-middle-x"></div>

                                    <button
                                        onClick={() => setShowUserModal(false)}
                                        className="position-absolute top-0 end-0 m-3 text-white opacity-75 hover-opacity-100 border-0 bg-white-20 p-2 radius-50 transition-all z-1"
                                    >
                                        <Icon icon="radix-icons:cross-2" width="20" height="20" />
                                    </button>
                                </div>

                                {/* Profile Section (Overlapping) */}
                                <div className="px-32 position-relative text-center" style={{ marginTop: '-50px' }}>
                                    <div className="d-inline-block position-relative">
                                        <div className="w-100-px h-100-px rounded-circle bg-white p-1 shadow-lg d-flex justify-content-center align-items-center mx-auto mb-3">
                                            <div className="w-100 h-100 rounded-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-5xl">
                                                <Icon icon="solar:user-bold" />
                                            </div>
                                        </div>
                                        <span className={`position-absolute bottom-0 end-0 mb-3 me-1 p-2 border border-2 border-white rounded-circle ${selectedUser.role === 'Doctor' ? 'bg-info-main' :
                                            selectedUser.role === 'Patient' ? 'bg-success-main' :
                                                selectedUser.role === 'ClinicAdmin' ? 'bg-primary-main' :
                                                    'bg-warning-main'
                                            }`}></span>
                                    </div>

                                    <h4 className="fw-bold text-dark mb-1">{selectedUser.name}</h4>
                                    <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
                                        <span className={`px-12 py-4 radius-20 text-xs fw-bold text-uppercase tracking-wider ${selectedUser.role === 'Doctor' ? 'bg-info-50 text-info-600' :
                                            selectedUser.role === 'Patient' ? 'bg-success-50 text-success-600' :
                                                selectedUser.role === 'ClinicAdmin' ? 'bg-primary-50 text-primary-600' :
                                                    'bg-warning-50 text-warning-600'
                                            }`}>
                                            {selectedUser.role}
                                        </span>
                                        <span className="text-secondary-light text-xs">•</span>
                                        <span className="text-secondary text-sm">ID: {selectedUser._id?.substring(0, 8)}</span>
                                    </div>
                                </div>

                                {/* Details Grid */}
                                <div className="px-32 pb-32">
                                    <div className="row g-4 pt-2">
                                        {/* Contact Column */}
                                        <div className="col-12">
                                            <h6 className="text-uppercase text-secondary-light text-xs fw-bolder mb-3 tracking-widest border-bottom pb-2">Contact Details</h6>
                                            <div className="d-flex flex-column gap-3">
                                                <div className="d-flex align-items-start gap-3 group-hover-bg transition-all p-2 radius-8 hover-bg-light">
                                                    <div className="text-primary-600 text-xl mt-1 opacity-75">
                                                        <Icon icon="solar:letter-bold" />
                                                    </div>
                                                    <div>
                                                        <label className="d-block text-secondary text-xs fw-medium mb-0">Email Address</label>
                                                        <span className="text-dark fw-semibold text-sm text-break">{selectedUser.email}</span>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-start gap-3 p-2 radius-8 hover-bg-light">
                                                    <div className="text-primary-600 text-xl mt-1 opacity-75">
                                                        <Icon icon="solar:phone-bold" />
                                                    </div>
                                                    <div>
                                                        <label className="d-block text-secondary text-xs fw-medium mb-0">Phone Number</label>
                                                        <span className="text-dark fw-semibold text-sm">{selectedUser.phone || 'Not Provided'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Professional / System Column */}
                                        <div className="col-12">
                                            <h6 className="text-uppercase text-secondary-light text-xs fw-bolder mb-3 tracking-widest border-bottom pb-2 mt-2">Professional Info</h6>
                                            <div className="d-flex flex-column gap-3">
                                                {/* Clinic Association (Only if applies) */}
                                                {(selectedUser.clinicId || (selectedUser.role === 'Doctor' || selectedUser.role === 'Assistant' || selectedUser.role === 'ClinicAdmin')) && (
                                                    <div className="d-flex align-items-start gap-3 p-2 radius-8 hover-bg-light">
                                                        <div className="text-primary-600 text-xl mt-1 opacity-75">
                                                            <Icon icon="solar:hospital-bold" />
                                                        </div>
                                                        <div>
                                                            <label className="d-block text-secondary text-xs fw-medium mb-0">Associated Clinic</label>
                                                            {selectedUser.clinicId ? (
                                                                <span className="text-dark fw-semibold text-sm">{selectedUser.clinicId.name}</span>
                                                            ) : (
                                                                <span className="text-secondary-light text-sm italic">No specific clinic linked</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="d-flex align-items-start gap-3 p-2 radius-8 hover-bg-light">
                                                    <div className="text-primary-600 text-xl mt-1 opacity-75">
                                                        <Icon icon="solar:calendar-bold" />
                                                    </div>
                                                    <div>
                                                        <label className="d-block text-secondary text-xs fw-medium mb-0">Join Date</label>
                                                        <span className="text-dark fw-semibold text-sm">{new Date(selectedUser.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Footer */}
                                    <div className="mt-4 pt-3 border-top">
                                        <Button
                                            variant="light"
                                            className="w-100 text-primary-600 fw-bold py-12 radius-12 d-flex align-items-center justify-content-center gap-2 hover-bg-primary-50 transition-all border-0"
                                            onClick={() => setShowUserModal(false)}
                                        >
                                            Close Details
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                </Modal >
                {/* Plan View Modal */}
                < Modal show={showPlanViewModal} onHide={() => setShowPlanViewModal(false)} centered size="lg" >
                    <Modal.Body className="p-0 bg-transparent border-0">
                        {selectedPlanView && (
                            <div className="position-relative">
                                <button
                                    onClick={() => setShowPlanViewModal(false)}
                                    className="position-absolute top-0 end-0 m-3 btn btn-sm btn-light rounded-circle shadow-sm z-index-1"
                                    style={{ zIndex: 10 }}
                                >
                                    <Icon icon="solar:close-circle-bold" className="text-xl" />
                                </button>
                                <PlanDetailCard plan={selectedPlanView} />
                            </div>
                        )}
                    </Modal.Body>
                </Modal >

                {/* Plan Create/Edit Modal */}
                < Modal show={showPlanModal} onHide={() => setShowPlanModal(false)} centered size="lg" >
                    <Modal.Header closeButton>
                        <Modal.Title>{editingPlanId ? 'Edit Subscription Plan' : 'Create New Plan'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handlePlanSubmit}>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Plan Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="e.g. Basic, Premium"
                                            value={planFormData.name}
                                            onChange={e => setPlanFormData({ ...planFormData, name: e.target.value })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Price <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="number"
                                            placeholder="999"
                                            value={planFormData.price}
                                            onChange={e => setPlanFormData({ ...planFormData, price: e.target.value })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Currency</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={planFormData.currency}
                                            onChange={e => setPlanFormData({ ...planFormData, currency: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Duration</Form.Label>
                                        <Form.Select
                                            value={planFormData.duration}
                                            onChange={e => setPlanFormData({ ...planFormData, duration: e.target.value })}
                                        >
                                            <option value="monthly">Monthly</option>
                                            <option value="yearly">Yearly</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Check
                                            type="switch"
                                            id="plan-status-switch"
                                            label={planFormData.isActive ? "Active" : "Inactive"}
                                            checked={planFormData.isActive !== undefined ? planFormData.isActive : true}
                                            onChange={e => setPlanFormData({ ...planFormData, isActive: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <h6 className="mt-2 text-primary">Limits & Quotas</h6>
                            <Row>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Max Doctors (0 = Unlimited)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={planFormData.maxDoctors}
                                            onChange={e => setPlanFormData({ ...planFormData, maxDoctors: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Max Staff (0 = Unlimited)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={planFormData.maxStaff}
                                            onChange={e => setPlanFormData({ ...planFormData, maxStaff: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Form.Group>
                                        <Form.Label>Storage (GB)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={planFormData.storageGB}
                                            onChange={e => setPlanFormData({ ...planFormData, storageGB: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Features (comma separated)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="e.g. Analytics, 24/7 Support, Custom Domain"
                                    value={planFormData.features}
                                    onChange={e => setPlanFormData({ ...planFormData, features: e.target.value })}
                                />
                            </Form.Group>
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={() => setShowPlanModal(false)}>Cancel</Button>
                                <Button variant="primary" type="submit">{editingPlanId ? 'Update Plan' : 'Create Plan'}</Button>
                            </div>
                        </Form>
                    </Modal.Body>
                </Modal >

                {/* Ticket Management Modal */}
                <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} centered size="lg">
                    <Modal.Header closeButton className="bg-base border-bottom py-3 px-4">
                        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                            <Icon icon="solar:chat-round-line-bold" className="text-primary" />
                            Ticket: {selectedTicket?.subject}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-0 bg-light">
                        {selectedTicket && (
                            <>
                                {/* Ticket Header Details */}
                                <div className="p-4 bg-white border-bottom shadow-sm">
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <label className="text-xs text-secondary fw-bold text-uppercase d-block mb-1">User Details</label>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="w-40-px h-40-px radius-8 bg-info-50 text-info-600 d-flex align-items-center justify-content-center fw-bold text-lg">
                                                    {selectedTicket?.userId?.name?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <p className="mb-0 fw-bold text-dark">{selectedTicket?.userId?.name}</p>
                                                    <p className="mb-0 text-xs text-secondary">{selectedTicket?.userId?.email}</p>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <label className="text-xs text-secondary fw-bold text-uppercase d-block mb-1">Clinic Info</label>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="w-40-px h-40-px radius-8 bg-warning-50 text-warning-600 d-flex align-items-center justify-content-center">
                                                    <Icon icon="solar:hospital-bold" className="text-xl" />
                                                </div>
                                                <div>
                                                    <p className="mb-0 fw-bold text-dark">{selectedTicket?.clinicId?.name || 'Individual'}</p>
                                                    <p className="mb-0 text-xs text-secondary">Priority: <span className={`fw-bold ${selectedTicket?.priority === 'High' ? 'text-danger' : 'text-warning'}`}>{selectedTicket?.priority}</span></p>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="mt-3 p-3 bg-primary-50 radius-8 border-start border-primary border-4">
                                        <p className="mb-1 text-xs text-primary fw-bold">Original Message:</p>
                                        <p className="mb-0 text-sm text-dark">{selectedTicket?.message}</p>
                                    </div>
                                </div>

                                {/* Chat Window */}
                                <div className="chat-window p-4" style={{ height: '400px', overflowY: 'auto', background: '#f8fafc', borderRadius: '12px', margin: '15px' }}>
                                    <div className="d-flex flex-column gap-3">
                                        {selectedTicket?.responses && selectedTicket.responses.length > 0 ? (
                                            selectedTicket.responses.map((resp, idx) => (
                                                <div key={idx} className={`d-flex ${resp.responder?.role === 'SuperAdmin' ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div className={`radius-12 p-3 ${resp.responder?.role === 'SuperAdmin' ? 'bg-primary text-white shadow-sm' : 'bg-white shadow-sm border'}`} style={{ maxWidth: '80%', position: 'relative' }}>
                                                        <div className="d-flex justify-content-between align-items-center gap-4 mb-2">
                                                            <span className={`text-xs fw-bold px-2 py-1 rounded ${resp.responder?.role === 'SuperAdmin' ? 'bg-white text-primary' : 'bg-primary-50 text-primary-600'}`}>
                                                                {resp.responder?.role === 'SuperAdmin' ? 'You' : resp.responder?.name}
                                                            </span>
                                                            <span className={`text-xs ${resp.responder?.role === 'SuperAdmin' ? 'text-white-50' : 'text-secondary'}`}>
                                                                {new Date(resp.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                        <p className="mb-0 text-sm" style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{resp.message}</p>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-5 text-secondary">
                                                <Icon icon="solar:chat-line-broken" className="display-4 mb-2 op-3" />
                                                <p className="text-sm">No conversation history yet.</p>
                                            </div>
                                        )}
                                        <div ref={chatEndRef} />
                                    </div>
                                </div>

                                {/* Reply Section */}
                                <div className="p-4 bg-white border-top">
                                    {selectedTicket?.status === 'Closed' ? (
                                        <div className="text-center py-3 bg-secondary-50 text-secondary-600 radius-8 border border-secondary-200 fw-bold text-sm">
                                            <Icon icon="solar:lock-bold" className="me-2 text-lg align-middle" />
                                            Ticket Closed by Clinical Admin
                                        </div>
                                    ) : (
                                        <Form onSubmit={handleTicketSubmit}>
                                            <Row className="g-3 align-items-end">
                                                <Col md={12}>
                                                    <Form.Group>
                                                        <Form.Label className="text-xs fw-bold text-uppercase text-secondary">Type your reply</Form.Label>
                                                        <div className="d-flex gap-2">
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                className="radius-8 shadow-none border-secondary-light"
                                                                placeholder="Write a message to the clinic..."
                                                                value={replyMessage}
                                                                onChange={(e) => setReplyMessage(e.target.value)}
                                                                style={{ resize: 'none' }}
                                                                required
                                                            />
                                                            <Button
                                                                variant="primary"
                                                                type="submit"
                                                                className="px-4 radius-8 fw-bold d-flex align-items-center gap-2"
                                                                disabled={!replyMessage.trim()}
                                                            >
                                                                <Icon icon="solar:plain-bold" className="text-xl" />
                                                                Send
                                                            </Button>
                                                        </div>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                </Modal>



                {/* Invoice Detail Modal */}
                < InvoiceDetailModal
                    show={showInvoiceModal}
                    onHide={() => setShowInvoiceModal(false)}
                    invoice={selectedInvoice}
                    onEmail={handleEmailInvoice}
                />
                <style>{`
                    .swal2-container {
                        z-index: 10000 !important;
                    }
                `}</style>
            </main >
        </section >
    );
};

export default SuperAdminDashboard;
